import { Component } from "react";
import { AlertTriangle, RefreshCw, Trash2, ArrowLeft } from "lucide-react";

/**
 * ErrorBoundary — captura erros de renderização (incluindo `insertBefore` causado
 * por extensões de tradução do navegador) e mostra fallback com ações úteis.
 *
 * Props:
 *  - children
 *  - title (opcional)         — título da mensagem
 *  - storageKeysToClear (opcional, string[]) — chaves de localStorage limpas pelo botão "Limpar estado local"
 *  - backHref (opcional)      — destino do botão "Voltar"
 */
export default class ErrorBoundary extends Component {
  state = { hasError: false, error: null };

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, errorInfo);
  }

  reset = () => {
    this.setState({ hasError: false, error: null });
  };

  clearLocal = () => {
    const keys = this.props.storageKeysToClear || [];
    try {
      keys.forEach((k) => localStorage.removeItem(k));
    } catch (_) { /* sessionStorage indisponível */ }
    this.reset();
    if (typeof window !== "undefined") window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    const msg = String(this.state.error?.message || this.state.error || "Erro desconhecido");
    return (
      <div className="card-mil p-6 gold-accent-top max-w-2xl mx-auto mt-8" data-testid="error-boundary">
        <div className="flex items-center gap-3 mb-3">
          <AlertTriangle size={28} className="text-[var(--danger)]" />
          <h2 className="font-display text-xl font-bold">
            {this.props.title || "Não foi possível carregar esta tela."}
          </h2>
        </div>
        <p className="text-sm text-[var(--text-muted)] mb-3">
          Tente recarregar a página ou limpar os dados da simulação. Se o erro persistir, contate o
          administrador do sistema.
        </p>
        <details className="text-xs font-mono bg-[var(--surface-2)] border border-[var(--border)] rounded p-2 mb-4">
          <summary className="cursor-pointer text-[var(--text-muted)]">Detalhes técnicos</summary>
          <div className="mt-2 break-all">{msg}</div>
        </details>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="btn-gold text-xs flex items-center gap-1.5"
            data-testid="boundary-reload"
          >
            <RefreshCw size={12} /> Recarregar página
          </button>
          {this.props.storageKeysToClear && this.props.storageKeysToClear.length > 0 && (
            <button
              type="button"
              onClick={this.clearLocal}
              className="btn-ghost text-xs flex items-center gap-1.5"
              data-testid="boundary-clear-local"
            >
              <Trash2 size={12} /> Limpar estado local
            </button>
          )}
          {this.props.backHref && (
            <a
              href={this.props.backHref}
              className="btn-ghost text-xs flex items-center gap-1.5"
              data-testid="boundary-back"
            >
              <ArrowLeft size={12} /> Voltar
            </a>
          )}
        </div>
      </div>
    );
  }
}
